import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Download, Mail, History, LogOut, Users, UserPlus, Pencil, Trash2, GitMerge, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ComposeEmailDialog } from "@/components/admin/ComposeEmailDialog";
import { EmailHistoryDialog } from "@/components/admin/EmailHistoryDialog";
import { StudentFormDialog } from "@/components/admin/StudentFormDialog";
import { StudentDetailDialog } from "@/components/admin/StudentDetailDialog";
import { MergeStudentsDialog } from "@/components/admin/MergeStudentsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

const selectCls = "px-3 py-2 rounded-md border bg-white text-sm text-slate-800";

function toCsv(rows, courseTitle) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = ["Name", "Email", "State", "License #", "AGD #", "Account Type", "Subscriber", "Courses Completed", "Courses In Progress", "Joined"];
  const lines = rows.map((r) => [
    r.name, r.email, r.state, r.license_number, r.agd_number,
    r.account_type === "lead" ? "Quick-register lead" : "Full account",
    r.is_subscriber ? "Yes" : "No",
    r.completed_course_ids.map(courseTitle).join("; "),
    r.in_progress_course_ids.map(courseTitle).join("; "),
    r.created_at ? new Date(r.created_at).toLocaleDateString() : "",
  ].map(esc).join(","));
  return [header.map(esc).join(","), ...lines].join("\n");
}

export default function AdminStudents({ logout }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("session_token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [contacts, setContacts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [accountType, setAccountType] = useState("all");
  const [subscriber, setSubscriber] = useState("all");
  const [completedCourse, setCompletedCourse] = useState("");
  const [inProgressCourse, setInProgressCourse] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

  const loadContacts = useCallback(
    () => axios.get(`${API}/admin/contacts`, { headers }).then((c) => setContacts(c.data)),
    [headers]
  );

  useEffect(() => {
    Promise.all([
      loadContacts(),
      axios.get(`${API}/admin/courses`, { headers }).then((k) => setCourses(k.data)),
    ]).catch(() => toast.error("Failed to load students")).finally(() => setLoading(false));
  }, [headers, loadContacts]);

  const openAdd = () => { setEditingStudent(null); setFormOpen(true); };
  const openEdit = (c) => { setEditingStudent(c); setFormOpen(true); };
  const handleDelete = async (c) => {
    if (!window.confirm(`Delete ${c.name || c.email}? This removes their account, progress and certificates permanently.`)) return;
    try {
      await axios.delete(`${API}/admin/students/${c.id}`, { headers });
      toast.success("Student deleted");
      setSelected((prev) => { const n = new Set(prev); n.delete(c.id); return n; });
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete student");
    }
  };

  const courseTitle = (id) => courses.find((c) => c.id === id)?.title || "Unknown course";

  const duplicateIds = useMemo(() => {
    const groups = {};
    contacts.forEach((c) => {
      const keyEmail = c.email.trim().toLowerCase();
      const keyName = c.name.trim().toLowerCase().replace(/^dr\.?\s+/, "");
      (groups[`e:${keyEmail}`] = groups[`e:${keyEmail}`] || []).push(c.id);
      if (keyName) (groups[`n:${keyName}`] = groups[`n:${keyName}`] || []).push(c.id);
    });
    const ids = new Set();
    Object.values(groups).forEach((g) => { if (g.length > 1) g.forEach((id) => ids.add(id)); });
    return ids;
  }, [contacts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (showDuplicatesOnly && !duplicateIds.has(c.id)) return false;
      if (q && !`${c.name} ${c.email} ${c.state} ${c.license_number}`.toLowerCase().includes(q)) return false;
      if (accountType !== "all" && c.account_type !== accountType) return false;
      if (subscriber === "yes" && !c.is_subscriber) return false;
      if (subscriber === "no" && c.is_subscriber) return false;
      if (completedCourse && !c.completed_course_ids.includes(completedCourse)) return false;
      if (inProgressCourse && !c.in_progress_course_ids.includes(inProgressCourse)) return false;
      return true;
    });
  }, [contacts, search, accountType, subscriber, completedCourse, inProgressCourse, showDuplicatesOnly, duplicateIds]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
  const toggleAll = () => {
    const next = new Set(selected);
    filtered.forEach((c) => (allFilteredSelected ? next.delete(c.id) : next.add(c.id)));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectedContacts = contacts.filter((c) => selected.has(c.id));

  const exportCsv = () => {
    const rows = selectedContacts.length ? selectedContacts : filtered;
    const blob = new Blob([toCsv(rows, courseTitle)], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `students_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success(`Exported ${rows.length} contact${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="admin-students-page">
      <header className="bg-white shadow-md border-b-4" style={{ borderColor: "#9E3639" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-8 sm:h-12" />
            <div>
              <h1 className="text-base sm:text-2xl font-bold" style={{ color: "#092036" }}>Students & Email</h1>
              <p className="text-xs sm:text-sm text-slate-600 hidden sm:block">Contact list, filters, bulk email and CSV export</p>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button size="sm" onClick={openAdd} className="gap-2 bg-[#102862] hover:bg-[#1a3a7a] text-white" data-testid="add-student-btn">
              <UserPlus className="w-4 h-4" /> Add Student
            </Button>
            <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} className="gap-2" data-testid="email-history-btn">
              <History className="w-4 h-4" /> History
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin")} className="gap-2" data-testid="back-to-admin-btn">
              <ArrowLeft className="w-4 h-4" /> Admin
            </Button>
            <Button variant="outline" size="sm" onClick={logout} className="gap-2" data-testid="admin-students-logout">
              <LogOut className="w-4 h-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {duplicateIds.size > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="duplicates-banner">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">
              <strong>{duplicateIds.size} records</strong> look like duplicates (same email or name). Select the matching rows and use <strong>Merge selected</strong> to combine them.
            </span>
            <Button variant="outline" size="sm" onClick={() => setShowDuplicatesOnly((v) => !v)} className="bg-white" data-testid="toggle-duplicates-btn">
              {showDuplicatesOnly ? "Show all students" : "Show possible duplicates"}
            </Button>
          </div>
        )}
        <div className="bg-white rounded-lg border p-4 grid gap-3 md:grid-cols-5" data-testid="student-filters">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search name, email, state, license" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="student-search" />
          </div>
          <select value={accountType} onChange={(e) => setAccountType(e.target.value)} className={selectCls} data-testid="filter-account-type">
            <option value="all">All account types</option>
            <option value="full">Full accounts</option>
            <option value="lead">Quick-register leads</option>
          </select>
          <select value={subscriber} onChange={(e) => setSubscriber(e.target.value)} className={selectCls} data-testid="filter-subscriber">
            <option value="all">Subscribers & non-subscribers</option>
            <option value="yes">All Access subscribers</option>
            <option value="no">Non-subscribers</option>
          </select>
          <select value={completedCourse} onChange={(e) => setCompletedCourse(e.target.value)} className={selectCls} data-testid="filter-completed-course">
            <option value="">Any completion status</option>
            {courses.map((c) => <option key={c.id} value={c.id}>Completed: {c.title}</option>)}
          </select>
          <select value={inProgressCourse} onChange={(e) => setInProgressCourse(e.target.value)} className={`${selectCls} md:col-span-2`} data-testid="filter-in-progress-course">
            <option value="">Any in-progress status</option>
            {courses.map((c) => <option key={c.id} value={c.id}>In progress (not finished): {c.title}</option>)}
          </select>
          <div className="md:col-span-3 flex flex-wrap items-center justify-end gap-2">
            <span className="text-sm text-slate-600 mr-auto" data-testid="student-count">
              <Users className="inline w-4 h-4 mr-1" />{filtered.length} of {contacts.length} students · {selected.size} selected
            </span>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length} className="gap-2" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV {selected.size ? `(${selected.size})` : `(${filtered.length})`}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMergeOpen(true)} disabled={selected.size < 2} className="gap-2" data-testid="merge-selected-btn">
              <GitMerge className="w-4 h-4" /> Merge selected {selected.size >= 2 ? `(${selected.size})` : ""}
            </Button>
            <Button size="sm" onClick={() => setComposeOpen(true)} disabled={!selected.size} className="gap-2 bg-[#102862] hover:bg-[#1a3a7a] text-white" data-testid="email-selected-btn">
              <Mail className="w-4 h-4" /> Email selected ({selected.size})
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border overflow-x-auto">
          {loading ? (
            <p className="p-8 text-center text-slate-500">Loading students...</p>
          ) : (
            <table className="w-full text-sm" data-testid="students-table">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="p-3 w-10"><input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} data-testid="select-all-checkbox" /></th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">State / License</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Completed</th>
                  <th className="p-3">In progress</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`student-row-${c.id}`}>
                    <td className="p-3"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} data-testid={`select-student-${c.id}`} /></td>
                    <td className="p-3 font-medium">
                      <button onClick={() => setDetailId(c.id)} className="text-left hover:underline" style={{ color: "#092036" }} data-testid={`student-name-${c.id}`}>
                        {c.name || "—"}
                      </button>
                      {duplicateIds.has(c.id) && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 align-middle" title="Possible duplicate">dup</span>}
                    </td>
                    <td className="p-3">
                      <a href={`mailto:${c.email}`} className="text-blue-700 hover:underline">{c.email}</a>
                    </td>
                    <td className="p-3 text-slate-600">{[c.state, c.license_number].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${c.account_type === "lead" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                        {c.account_type === "lead" ? "Lead" : "Full"}
                      </span>
                      {c.is_subscriber && <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">Subscriber</span>}
                    </td>
                    <td className="p-3 text-slate-600" title={c.completed_course_ids.map(courseTitle).join(", ")}>{c.completed_course_ids.length}</td>
                    <td className="p-3 text-slate-600" title={c.in_progress_course_ids.map(courseTitle).join(", ")}>{c.in_progress_course_ids.length}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)} title="Edit" data-testid={`edit-student-${c.id}`}>
                          <Pencil className="w-4 h-4 text-slate-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(c)} title="Delete" data-testid={`delete-student-${c.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-slate-500" data-testid="no-students">No students match these filters.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <ComposeEmailDialog open={composeOpen} onClose={() => setComposeOpen(false)} recipients={selectedContacts} />
      <EmailHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <StudentFormDialog open={formOpen} onClose={() => setFormOpen(false)} student={editingStudent} onSaved={loadContacts} />
      <StudentDetailDialog studentId={detailId} onClose={() => setDetailId(null)} onEdit={(st) => { setDetailId(null); openEdit(st); }} />
      <MergeStudentsDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        students={selectedContacts}
        onMerged={() => { setSelected(new Set()); loadContacts(); }}
      />
    </div>
  );
}
